/**
 * Anexos do chat (RF11) — classificação, limites e processamento.
 *
 * Conforme o tipo de arquivo:
 *  - IMAGEM (png/jpg/gif/webp)  → bloco nativo `image` para o Bedrock;
 *  - PDF                        → bloco nativo `document` para o Bedrock;
 *  - MARKDOWN/TXT               → texto lido no próprio cliente;
 *  - DOCX (e variantes OPC)     → texto extraído pelo backend
 *                                 (POST /attachments/extract, python-docx).
 * Qualquer outro formato é rejeitado com mensagem clara.
 *
 * Limites (validados AQUI, antes de qualquer upload/parse):
 *  - máx. 3 arquivos por prompt; 30MB somados;
 *  - imagem: 10MB cada; PDF: 20MB cada; md/txt/docx: 20MB cada;
 *  - texto extraído: teto de caracteres (evita estourar o contexto — o mesmo
 *    princípio da leitura por janela de documentos grandes).
 *
 * Escopo: os limites acima valem POR ENVIO. Depois de enviados, os anexos
 * pertencem à CONVERSA e seguem disponíveis em todos os pedidos, num bloco
 * fixo no início das mensagens — ver `util/attachmentContext.ts`, que também
 * guarda os tetos da conversa inteira.
 */
import { extractAttachmentText } from '../api/client';
import { formatBytes } from './attachmentContext';
// Re-exportado: o painel importa daqui, e a função mora no módulo puro para os
// checks rodarem fora do Word (este arquivo puxa o cliente HTTP).
export { formatBytes };
export const MAX_FILES = 3;
export const MAX_TOTAL_BYTES = 30 * 1024 * 1024;
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
export const MAX_PDF_BYTES = 20 * 1024 * 1024;
export const MAX_TEXTDOC_BYTES = 20 * 1024 * 1024;
/** Teto do texto que entra como contexto por anexo (~15-20k tokens). */
export const MAX_TEXT_CHARS = 60000;
const IMAGE_TYPES = {
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    webp: 'image/webp',
};
const TEXT_EXTS = new Set(['md', 'markdown', 'txt']);
const DOCX_EXTS = new Set(['docx', 'docm', 'dotx', 'dotm']);
/** Lista para o atributo accept do <input type=file>. */
export const ACCEPT_ATTR = [
    '.png', '.jpg', '.jpeg', '.gif', '.webp',
    '.pdf', '.md', '.markdown', '.txt',
    '.docx', '.docm', '.dotx', '.dotm',
].join(',');
function fileExt(name) {
    const i = name.lastIndexOf('.');
    return i >= 0 ? name.slice(i + 1).toLowerCase() : '';
}
/** Decide tipo/destino/limite do arquivo — ou o motivo da rejeição. */
export function classifyFile(name, _mime) {
    const ext = fileExt(name);
    if (IMAGE_TYPES[ext]) {
        return { ok: true, kind: 'image', mediaType: IMAGE_TYPES[ext], maxBytes: MAX_IMAGE_BYTES, viaBackend: false };
    }
    if (ext === 'pdf') {
        return { ok: true, kind: 'pdf', mediaType: 'application/pdf', maxBytes: MAX_PDF_BYTES, viaBackend: false };
    }
    if (TEXT_EXTS.has(ext)) {
        return { ok: true, kind: 'text', mediaType: 'text/plain', maxBytes: MAX_TEXTDOC_BYTES, viaBackend: false };
    }
    if (DOCX_EXTS.has(ext)) {
        return {
            ok: true,
            kind: 'text',
            mediaType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            maxBytes: MAX_TEXTDOC_BYTES,
            viaBackend: true,
        };
    }
    if (ext === 'doc') {
        return { ok: false, error: 'Formato .doc (Word antigo) não é suportado — salve como .docx e anexe de novo.' };
    }
    return {
        ok: false,
        error: `Formato .${ext || '?'} não suportado. Aceitos: imagens (png/jpg/gif/webp), PDF, Markdown, TXT e DOCX.`,
    };
}
/** Valida contagem e total ANTES de processar. Devolve mensagem de erro ou null. */
export function validateBatch(current, incoming) {
    if (current.length + incoming.length > MAX_FILES) {
        return `Máximo de ${MAX_FILES} arquivos por mensagem.`;
    }
    const total = current.reduce((s, a) => s + a.size, 0) + incoming.reduce((s, f) => s + f.size, 0);
    if (total > MAX_TOTAL_BYTES) {
        return `Anexos somam ${formatBytes(total)} — o máximo por mensagem é ${formatBytes(MAX_TOTAL_BYTES)}.`;
    }
    return null;
}
function readAsBase64(file) {
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onerror = () => reject(new Error('Falha ao ler o arquivo.'));
        r.onload = () => {
            const url = String(r.result || '');
            const i = url.indexOf('base64,');
            if (i < 0)
                return reject(new Error('Falha ao codificar o arquivo.'));
            resolve(url.slice(i + 'base64,'.length));
        };
        r.readAsDataURL(file);
    });
}
function readAsText(file) {
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onerror = () => reject(new Error('Falha ao ler o arquivo.'));
        r.onload = () => resolve(String(r.result || ''));
        r.readAsText(file); // UTF-8
    });
}
let attachSeq = 0;
/**
 * Processa UM arquivo já classificado: valida tamanho, lê/extrai o conteúdo.
 * Nunca lança — erros viram status 'error' no próprio anexo (vira chip de erro
 * na UI; o envio é bloqueado enquanto houver erro/processamento).
 */
export async function processFile(file) {
    const base = {
        id: `att-${Date.now().toString(36)}-${attachSeq++}`,
        name: file.name,
        size: file.size,
        kind: 'text',
        mediaType: '',
        status: 'processing',
    };
    const cls = classifyFile(file.name, file.type);
    if (!cls.ok)
        return Object.assign(Object.assign({}, base), { status: 'error', error: cls.error });
    base.kind = cls.kind;
    base.mediaType = cls.mediaType;
    if (file.size > cls.maxBytes) {
        return Object.assign(Object.assign({}, base), { status: 'error', error: `${file.name}: ${formatBytes(file.size)} excede o limite de ${formatBytes(cls.maxBytes)} para ${cls.kind === 'image' ? 'imagens' : cls.kind === 'pdf' ? 'PDFs' : 'documentos'}.` });
    }
    if (file.size === 0)
        return Object.assign(Object.assign({}, base), { status: 'error', error: 'Arquivo vazio.' });
    try {
        if (cls.kind === 'image' || cls.kind === 'pdf') {
            const dataBase64 = await readAsBase64(file);
            return Object.assign(Object.assign({}, base), { dataBase64, status: 'ready' });
        }
        if (cls.viaBackend) {
            // DOCX: extração estruturada no backend (python-docx).
            const dataBase64 = await readAsBase64(file);
            const res = await extractAttachmentText(file.name, dataBase64);
            return Object.assign(Object.assign({}, base), { text: res.text, truncated: res.truncated, status: 'ready' });
        }
        // Markdown/TXT: leitura direta no cliente, com o MESMO teto de contexto.
        const raw = await readAsText(file);
        const truncated = raw.length > MAX_TEXT_CHARS;
        return Object.assign(Object.assign({}, base), { text: truncated ? raw.slice(0, MAX_TEXT_CHARS) : raw, truncated, status: 'ready' });
    }
    catch (e) {
        return Object.assign(Object.assign({}, base), { status: 'error', error: e instanceof Error ? e.message : String(e) });
    }
}
/** Teto do registro: o mesmo do lote de anexos, para o painel não crescer sem fim. */
const MAX_REGISTRY_BYTES = MAX_TOTAL_BYTES;
let imageRegistry = [];
/**
 * Guarda as imagens de um turno para uso posterior por `insert_image`.
 * Mantém as MAIS RECENTES dentro do teto: o pedido "insere aquela imagem"
 * costuma se referir à última anexada.
 */
export function rememberInsertableImages(attachments) {
    const novos = attachments
        .filter((a) => a.kind === 'image' && a.status === 'ready' && a.dataBase64)
        .map((a) => ({
        name: a.name,
        mediaType: a.mediaType,
        dataBase64: a.dataBase64,
        size: a.size,
    }));
    if (!novos.length)
        return;
    // Nome repetido: a anexada agora substitui a antiga (é o que o usuário quer
    // dizer com "a logo.png" depois de anexar outra logo.png).
    const nomes = new Set(novos.map((n) => n.name));
    imageRegistry = [...novos, ...imageRegistry.filter((i) => !nomes.has(i.name))];
    let total = 0;
    imageRegistry = imageRegistry.filter((img) => {
        total += img.size;
        return total <= MAX_REGISTRY_BYTES;
    });
}
/** Imagens que `insert_image` pode inserir agora (mais recente primeiro). */
export function listInsertableImages() {
    return imageRegistry;
}
/**
 * Resolve qual imagem inserir. Sem `name`, usa a mais recente — é o caso comum
 * ("insere a imagem que mandei"). Com `name`, casa por nome exato e, se não
 * achar, por sufixo/trecho (o modelo às vezes cita o nome sem a extensão).
 */
export function findInsertableImage(name) {
    var _a, _b;
    if (!imageRegistry.length)
        return null;
    if (!(name === null || name === void 0 ? void 0 : name.trim()))
        return imageRegistry[0];
    const alvo = name.trim().toLowerCase();
    return ((_b = (_a = imageRegistry.find((i) => i.name.toLowerCase() === alvo)) !== null && _a !== void 0 ? _a : imageRegistry.find((i) => i.name.toLowerCase().includes(alvo))) !== null && _b !== void 0 ? _b : null);
}
/** Esquece as imagens (chamado em "Nova conversa"). */
export function clearInsertableImages() {
    imageRegistry = [];
}
